/**
 * @file Dialog.tsx
 * @brief Modal dialog primitives (Radix Dialog wrapper).
 *
 * @details
 * Standard shadcn-style dialog set: Root / Trigger / Portal / Overlay /
 * Content / Header / Title / Description / Footer / Close. Used by every
 * "create / edit" form across the panel. Content supports `motion="fade"`
 * for pickers (no zoom).
 *
 * @dependencies
 * - @radix-ui/react-dialog
 * - lucide-react (X icon)
 * - shared/theme/cn
 *
 * @author DES
 * @date 26-05-2026
 * @updated 30-07-2026 — DialogContent motion fade|zoom
 */

import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type HTMLAttributes,
} from "react";
import { cn } from "@/shared/theme/cn";
import {
  keepOpenWhileMenuUp,
  restoreBodyInteraction,
} from "@/shared/utils/restoreBodyInteraction";

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

// ----------------------------------------------------------------------
// Overlay — dimmed full-screen backdrop
// ----------------------------------------------------------------------
export const DialogOverlay = forwardRef<
  ElementRef<typeof DialogPrimitive.Overlay>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/60 backdrop-blur-sm",
      "data-[state=open]:animate-in data-[state=open]:fade-in-0",
      "data-[state=closed]:animate-out data-[state=closed]:fade-out-0",
      "data-[state=closed]:pointer-events-none",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = "DialogOverlay";

// ----------------------------------------------------------------------
// Content — centered modal panel
// ----------------------------------------------------------------------
export const DialogContent = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    /**
     * Enter/exit motion. `zoom` is the default dialog pop (~5% scale).
     * `fade` = instant panel (no content animation) — used for picker A/B.
     */
    motion?: "zoom" | "fade";
    /** Leave out the corner ✕, for a dialog that draws its own (21-10-2026). */
    hideClose?: boolean;
  }
>(({ className, children, onCloseAutoFocus, onPointerDownOutside, motion = "zoom", hideClose = false, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid max-h-[90dvh] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 overflow-y-auto border bg-background p-6 shadow-lg sm:rounded-lg",
        // `fade` = no content motion (instant). Zoom-in-95 is only ~5% scale,
        // so "fade only" looked identical in the CountryPicker trial.
        motion === "fade"
          ? "data-[state=closed]:pointer-events-none"
          : [
              "data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
              "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95",
              "data-[state=closed]:pointer-events-none",
            ],
        className,
      )}
      onCloseAutoFocus={(event) => {
        onCloseAutoFocus?.(event);
        event.preventDefault();
        restoreBodyInteraction();
      }}
      onPointerDownOutside={(event) => {
        // A press that only closes an open menu must not close the form.
        if (keepOpenWhileMenuUp(event)) return;
        onPointerDownOutside?.(event);
      }}
      {...props}
    >
      {children}
      {hideClose ? null : (
      <DialogPrimitive.Close
        className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        aria-label="Close"
      >
        <X className="h-4 w-4" />
      </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = "DialogContent";

// ----------------------------------------------------------------------
// Layout helpers
// ----------------------------------------------------------------------
export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex flex-col space-y-1.5 text-left", className)}
      {...props}
    />
  );
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2",
        className,
      )}
      {...props}
    />
  );
}

export const DialogTitle = forwardRef<
  ElementRef<typeof DialogPrimitive.Title>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = "DialogTitle";

export const DialogDescription = forwardRef<
  ElementRef<typeof DialogPrimitive.Description>,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = "DialogDescription";
